/**
 * NÛRA — Collection page controller
 * ---------------------------------------------------------------------------
 * Progressive enhancement over plain Shopify markup:
 *   - Filter <form method="get"> and pagination <a> links work with JS off.
 *   - With JS on, filter/sort changes and "load more" fetch the section via
 *     Shopify's Section Rendering API (?section_id=...) and swap only the
 *     relevant DOM nodes, keeping the URL, browser history and back/forward
 *     navigation correct.
 *
 * AUTOMATIC SORT ROTATION — documented limitation:
 *   Shopify collection pages can be cached at the edge/CDN, and Liquid has no
 *   reliable per-request "first visit" signal. Re-sorting on every full page
 *   load would fight that cache and could reshuffle products mid-browse.
 *   Instead: the server always renders the merchant's chosen FALLBACK sort
 *   (cacheable, crawlable, correct for SEO). On load, THIS script computes a
 *   deterministic UTC time-bucket client-side and — only if the visitor has
 *   not manually picked a sort (no `sort_by` in the URL) — silently fetches
 *   the rotated sort order and swaps the grid in place, without touching
 *   history. Refreshing or sharing a link always returns the stable fallback
 *   sort unless the visitor's own choice is present in the URL. This is the
 *   safest approximation available without a server-side scheduled job.
 */
(() => {
  'use strict';

  const SECTION_PARAM = 'section_id';
  const SORT_UNIT_MS = { hours: 3600000, days: 86400000 };

  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isDataSaver() {
    return !!(navigator.connection && navigator.connection.saveData);
  }

  class NuraCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.baseUrl = this.dataset.url;
      this.filtersEnabled = this.dataset.filtersEnabled === 'true';
      this.filterLayout = this.dataset.filterLayout;
      this.infiniteEnabled = this.dataset.infiniteScroll === 'true';
      this.infiniteThreshold = parseInt(this.dataset.infiniteThreshold, 10) || 600;
      this.autoSortEnabled = this.dataset.autoSortEnabled === 'true';
      this.autoSortModes = (this.dataset.autoSortModes || '')
        .split('\n').map((s) => s.trim()).filter(Boolean);
      this.autoSortInterval = parseFloat(this.dataset.autoSortInterval) || 24;
      this.autoSortUnit = this.dataset.autoSortUnit || 'hours';
      this.autoSortFallback = this.dataset.autoSortFallback;
      this.animationStyle = this.dataset.animation || 'none';
      this.animationMobile = this.dataset.animationMobile === 'true';
      this.animationDuration = parseInt(this.dataset.animationDuration, 10) || 450;
      this.animationDelay = parseInt(this.dataset.animationDelay, 10) || 0;
      this.animationStagger = parseInt(this.dataset.animationStagger, 10) || 0;
      this.mobileVideoAutoplay = this.dataset.videoAutoplayMobile === 'true';

      this.isLoading = false;
      this.loadedIds = new Set();

      this.cacheRefs();
      this.setAnimationVars();
      this.bindFilterForm();
      this.bindSort();
      this.bindGridSwitcher();
      this.bindFilterPanel();
      this.bindChipRemoval();
      this.bindInfiniteScroll();
      this.bindFloatingNav();
      this.bindCardVideoHover();
      this.setupMobileVideoAutoplay();
      this.setupRevealAnimations();
      this.trackLoadedIds();
      this.maybeApplyAutoSortRotation();

      window.addEventListener('popstate', () => {
        this.applyFromUrl(location.href, { push: false });
      });
    }

    cacheRefs() {
      this.grid = this.querySelector('[data-nura-grid]');
      this.gridWrapper = this.querySelector('[data-nura-grid-wrapper]');
      this.pagination = this.querySelector('[data-nura-pagination]');
      this.filterPanel = this.querySelector('[data-nura-filter-panel]');
      this.filterForm = this.querySelector('[data-nura-filter-form]');
      this.activeBar = this.querySelector('[data-nura-active-filters-bar]');
      this.countEl = this.querySelector('[data-nura-count]');
      this.activeCountBadge = this.querySelector('[data-nura-active-count]');
    }

    setAnimationVars() {
      if (this.grid) {
        this.grid.style.setProperty('--nura-stagger', this.animationStagger + 'ms');
        this.grid.style.setProperty('--nura-anim-duration', this.animationDuration + 'ms');
      }
    }

    /* ---------------------------------------------------------------------
     * Networking: fetch a page via the Section Rendering API and swap parts
     * ------------------------------------------------------------------- */
    buildSectionUrl(url) {
      const u = new URL(url, location.origin);
      u.searchParams.set(SECTION_PARAM, this.sectionId);
      return u.toString();
    }

    async fetchDoc(url) {
      const res = await fetch(this.buildSectionUrl(url), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      const html = await res.text();
      return new DOMParser().parseFromString(html, 'text/html');
    }

    async applyFromUrl(url, { push = true, silentSort = false } = {}) {
      if (this.isLoading) return;
      this.isLoading = true;
      this.gridWrapper && this.gridWrapper.setAttribute('aria-busy', 'true');

      try {
        const doc = await this.fetchDoc(url);
        this.swap(doc, '[data-nura-grid]');
        this.swap(doc, '[data-nura-pagination]');
        this.swap(doc, '[data-nura-active-filters-bar]');
        this.swap(doc, '[data-nura-filter-panel]', { keepOpenState: true });
        this.swap(doc, '[data-nura-count]');

        this.cacheRefs();
        this.setAnimationVars();
        this.trackLoadedIds();
        this.bindInfiniteScroll();
        this.setupMobileVideoAutoplay();
        this.setupRevealAnimations();
        this.updateActiveCount();

        if (!silentSort) {
          if (push) {
            history.pushState({ nuraCollection: true }, '', url);
          } else {
            history.replaceState({ nuraCollection: true }, '', url);
          }
        }

        this.announce();
      } catch (err) {
        // Network hiccup: fall back to a real navigation so the visitor
        // never gets stuck on a stale grid.
        location.href = url;
      } finally {
        this.isLoading = false;
        this.gridWrapper && this.gridWrapper.removeAttribute('aria-busy');
      }
    }

    swap(doc, selector, opts = {}) {
      const next = doc.querySelector(selector);
      const current = this.querySelector(selector);
      if (!next || !current) return;
      if (opts.keepOpenState && current.getAttribute('data-open') === 'true') {
        next.setAttribute('data-open', 'true');
      }
      current.replaceWith(next);
    }

    announce() {
      const status = this.querySelector('[data-nura-load-status]');
      const count = this.countEl ? this.countEl.textContent.trim() : '';
      if (status) status.textContent = count;
    }

    /* ---------------------------------------------------------------------
     * Filters — instant apply, no Apply button
     * ------------------------------------------------------------------- */
    bindFilterForm() {
      if (!this.filtersEnabled) return;
      const debouncedPrice = debounce(() => this.submitFilters(), 500);

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-nura-filter-input]')) {
          e.preventDefault();
          this.submitFilters();
        }
      });

      this.addEventListener('input', (e) => {
        if (e.target.matches('[data-nura-price-min], [data-nura-price-max]')) {
          debouncedPrice();
        }
      });

      this.addEventListener('submit', (e) => {
        if (e.target.matches('[data-nura-filter-form]')) {
          e.preventDefault();
          this.submitFilters();
        }
      });
    }

    submitFilters() {
      const form = this.querySelector('[data-nura-filter-form]');
      if (!form) return;
      const params = new URLSearchParams(new FormData(form));
      const url = `${this.baseUrl}?${params.toString()}`;
      this.applyFromUrl(url, { push: true });
    }

    bindChipRemoval() {
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-nura-chip] a, [data-nura-clear-all]');
        if (!link) return;
        e.preventDefault();
        this.applyFromUrl(link.href, { push: true });
      });
    }

    updateActiveCount() {
      if (!this.activeCountBadge) return;
      const chips = this.querySelectorAll('[data-nura-active-filters-list] [data-nura-chip]');
      if (chips.length) {
        this.activeCountBadge.hidden = false;
        this.activeCountBadge.textContent = chips.length;
      } else {
        this.activeCountBadge.hidden = true;
      }
    }

    /* ---------------------------------------------------------------------
     * Sort
     * ------------------------------------------------------------------- */
    bindSort() {
      this.addEventListener('change', (e) => {
        if (!e.target.matches('[data-nura-sort]')) return;
        const url = new URL(this.baseUrl, location.origin);
        // preserve current filters when changing sort
        const current = new URLSearchParams(location.search);
        current.set('sort_by', e.target.value);
        url.search = current.toString();
        this.applyFromUrl(url.toString(), { push: true });
      });
    }

    /* ---------------------------------------------------------------------
     * Automatic default-sort rotation (client-side only — see file header)
     * ------------------------------------------------------------------- */
    maybeApplyAutoSortRotation() {
      if (!this.autoSortEnabled || !this.autoSortModes.length) return;
      const params = new URLSearchParams(location.search);
      if (params.has('sort_by')) return; // visitor already has an explicit sort — never override

      const intervalMs = this.autoSortInterval * (SORT_UNIT_MS[this.autoSortUnit] || SORT_UNIT_MS.hours);
      const bucket = Math.floor(Date.now() / intervalMs) % this.autoSortModes.length;
      const chosen = this.autoSortModes[bucket];
      if (!chosen || chosen === this.autoSortFallback) return;

      const url = new URL(this.baseUrl, location.origin);
      url.searchParams.set('sort_by', chosen);
      this.applyFromUrl(url.toString(), { push: false, silentSort: true }).then(() => {
        const select = this.querySelector('[data-nura-sort]');
        if (select) select.value = chosen;
      });
    }

    /* ---------------------------------------------------------------------
     * Grid density switcher (client-side, instant, no reload)
     * ------------------------------------------------------------------- */
    bindGridSwitcher() {
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-cols]');
        if (!btn) return;
        const cols = btn.dataset.cols;
        const breakpoint = btn.closest('[data-breakpoint]');
        const bp = breakpoint ? breakpoint.dataset.breakpoint : 'desktop';
        if (this.grid) {
          this.grid.style.setProperty(`--nura-cols-${bp}`, cols);
        }
        btn.parentElement.querySelectorAll('[data-cols]').forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        try {
          sessionStorage.setItem(`nura-grid-cols-${bp}`, cols);
        } catch (err) { /* storage unavailable — non-critical */ }
      });

      ['desktop', 'tablet', 'mobile'].forEach((bp) => {
        try {
          const saved = sessionStorage.getItem(`nura-grid-cols-${bp}`);
          if (saved && this.grid) this.grid.style.setProperty(`--nura-cols-${bp}`, saved);
        } catch (err) { /* storage unavailable — non-critical */ }
      });
    }

    /* ---------------------------------------------------------------------
     * Filter panel open/close (drawer / mobile / desktop-drawer layouts)
     * ------------------------------------------------------------------- */
    bindFilterPanel() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-nura-filter-toggle], [data-nura-fnav-filter]')) {
          this.openFilterPanel(e.target.closest('button'));
        }
        if (e.target.closest('[data-nura-filter-close], [data-nura-filter-backdrop], [data-nura-filter-apply]')) {
          this.closeFilterPanel();
        }
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeFilterPanel();
      });
    }

    openFilterPanel(trigger) {
      const panel = this.querySelector('[data-nura-filter-panel]');
      if (!panel) return;
      panel.setAttribute('data-open', 'true');
      panel.removeAttribute('aria-hidden');
      this._filterTrigger = trigger;
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      const firstInput = panel.querySelector('input, button, select, a');
      firstInput && firstInput.focus();
      document.body.style.overflow = 'hidden';
    }

    closeFilterPanel() {
      const panel = this.querySelector('[data-nura-filter-panel]');
      if (!panel) return;
      panel.removeAttribute('data-open');
      panel.setAttribute('aria-hidden', 'true');
      if (this._filterTrigger) {
        this._filterTrigger.setAttribute('aria-expanded', 'false');
        this._filterTrigger.focus();
      }
      document.body.style.overflow = '';
    }

    /* ---------------------------------------------------------------------
     * Infinite scroll
     * ------------------------------------------------------------------- */
    bindInfiniteScroll() {
      if (!this.infiniteEnabled) return;
      const sentinel = this.querySelector('[data-nura-sentinel]');
      if (!sentinel) return;
      if (this.io) this.io.disconnect();
      this.io = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) this.loadMore();
        },
        { rootMargin: `${this.infiniteThreshold}px` }
      );
      this.io.observe(sentinel);
    }

    trackLoadedIds() {
      this.querySelectorAll('[data-nura-card]').forEach((card) => {
        if (card.dataset.productId) this.loadedIds.add(card.dataset.productId);
      });
    }

    async loadMore() {
      const loadMoreLink = this.querySelector('[data-nura-load-more]');
      if (!loadMoreLink || this.isLoading) return;
      this.isLoading = true;

      const status = this.querySelector('[data-nura-load-status]');
      if (status) status.textContent = 'Loading more products…';

      try {
        const doc = await this.fetchDoc(loadMoreLink.href);
        const newGrid = doc.querySelector('[data-nura-grid]');
        const newPagination = doc.querySelector('[data-nura-pagination]');

        if (newGrid && this.grid) {
          let added = 0;
          Array.from(newGrid.children).forEach((child) => {
            const id = child.dataset ? child.dataset.productId : null;
            if (id && this.loadedIds.has(id)) return; // avoid duplicates
            if (id) this.loadedIds.add(id);
            this.grid.appendChild(child);
            added += 1;
          });
          this.setupMobileVideoAutoplay();
          this.setupRevealAnimations();
          if (status) status.textContent = `${added} more products loaded`;
        }

        if (newPagination && this.pagination) {
          this.pagination.replaceWith(newPagination);
          this.pagination = newPagination;
        }

        // Reflect the loaded page in the URL so a refresh/back keeps context,
        // without adding a history entry per batch.
        history.replaceState({ nuraCollection: true }, '', loadMoreLink.href);
        this.bindInfiniteScroll();
      } catch (err) {
        // Fallback: let the real <a href> work as a normal paginated link.
      } finally {
        this.isLoading = false;
      }
    }

    /* ---------------------------------------------------------------------
     * Card media: desktop hover video, mobile scroll-into-view autoplay
     * ------------------------------------------------------------------- */
    bindCardVideoHover() {
      this.addEventListener('mouseenter', (e) => {
        const media = e.target.closest && e.target.closest('[data-nura-card-media][data-has-video="true"]');
        if (!media || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        if (prefersReducedMotion()) return;
        const video = media.querySelector('[data-nura-card-video]');
        if (!video) return;
        if (!video.src && video.querySelector('source')) {
          video.load();
        }
        video.play().then(() => video.classList.add('is-playing')).catch(() => {});
      }, true);

      this.addEventListener('mouseleave', (e) => {
        const media = e.target.closest && e.target.closest('[data-nura-card-media][data-has-video="true"]');
        if (!media) return;
        const video = media.querySelector('[data-nura-card-video]');
        if (!video) return;
        video.pause();
        video.currentTime = 0;
        video.classList.remove('is-playing');
      }, true);
    }

    setupMobileVideoAutoplay() {
      if (!this.mobileVideoAutoplay) return;
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) return; // desktop uses hover instead
      if (prefersReducedMotion() || isDataSaver()) return;

      if (this._mobileVideoIo) this._mobileVideoIo.disconnect();
      const videos = this.querySelectorAll('[data-nura-card-video]');
      if (!videos.length) return;

      this._mobileVideoIo = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
              if (!video.src && video.querySelector('source')) video.load();
              video.play().then(() => video.classList.add('is-playing')).catch(() => {});
            } else {
              video.pause();
              video.classList.remove('is-playing');
            }
          });
        },
        { threshold: [0, 0.6, 1] }
      );
      videos.forEach((v) => this._mobileVideoIo.observe(v));
    }

    /* ---------------------------------------------------------------------
     * Reveal animations
     * ------------------------------------------------------------------- */
    setupRevealAnimations() {
      if (this.animationStyle === 'none') return;
      if (prefersReducedMotion()) return;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      if (isMobile && !this.animationMobile) return;

      const cards = this.querySelectorAll('.nura-product-card:not(.is-visible)');
      if (!cards.length) return;

      const className = {
        fade: 'nura-anim-fade',
        slide: 'nura-anim-slide',
        zoom: 'nura-anim-zoom',
        lift: 'nura-anim-lift',
        stagger: 'nura-anim-slide',
      }[this.animationStyle] || 'nura-anim-fade';

      if (this._revealIo) this._revealIo.disconnect();
      this._revealIo = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add(className, 'is-visible');
              obs.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
      );
      cards.forEach((card) => this._revealIo.observe(card));
    }

    /* ---------------------------------------------------------------------
     * Floating action navigation
     * ------------------------------------------------------------------- */
    bindFloatingNav() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-nura-fnav-sort]')) {
          const select = this.querySelector('[data-nura-sort]');
          select && select.focus();
        }
        if (e.target.closest('[data-nura-fnav-grid]')) {
          const switcher = this.querySelector('[data-nura-grid-switcher]');
          switcher && switcher.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
        }
        if (e.target.closest('[data-nura-fnav-top]')) {
          window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        }
      });
    }
  }

  if (!customElements.get('nura-collection')) {
    customElements.define('nura-collection', NuraCollection);
  }
})();
